import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';

import Maps from 'components/maps/Maps';
import { MapContainer } from 'components/maps/styled/Map.styled';
import { getGeocodePosition, getWidgetContainer } from 'components/maps/utils/mapHelpers';

import useMediaQuery from 'hooks/useMediaQuery';

import { setLoading } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { faLocation } from '@fortawesome/pro-solid-svg-icons';

import AddressInput from './AddressInput';
import Button from './Button';

const MapField = (props, ref) => {
	const {
		value,
		setValue,
		showAutocomplete = true,
		showLocationButton = true,
		restrictions = null,
		zoom = undefined,
		fullscreenControl = false,
		defaultCountry,
		disabled = false
	} = props;
	const maps = window?.google?.maps;

	const [inputValue, setInputValue] = useState('');
	const [marker, setMarker] = useState(null);
	const [map, setMap] = useState(null);
	const dispatch = useDispatch();
	const tabletView = useMediaQuery(Breakpoints.tablet);

	const inputContainer = useMemo(() => getWidgetContainer('100%', false, false, tabletView), [tabletView]);

	const getMarkerPosition = (latlng) => ({ lat: latlng.lat, lng: latlng.lng });
	const initMapFunctionality = async () => {
		const { AdvancedMarkerElement } = await maps.importLibrary('marker');

		const marker = new AdvancedMarkerElement({
			map,
			position: map?.getCenter() || { lat: 24.69664246, lng: 46.6827321 }
		});

		setMarker(marker);

		marker.content.classList.add('bounce');

		if (map.controls[maps.ControlPosition.TOP_CENTER].length === 0)
			map.controls[maps.ControlPosition.TOP_CENTER].push(inputContainer);

		if (map.controls[maps.ControlPosition.TOP_LEFT].length === 0)
			map.controls[maps.ControlPosition.TOP_LEFT].push(inputContainer);

		map.addListener('click', (e) => {
			if (disabled) return;
			const latlng = new maps.LatLng(e.latLng);
			setValue({ lat: latlng.lat(), lng: latlng.lng() });
		});
	};

	const onPlaceChange = async (place, text = null) => {
		if (!place) return;
		setInputValue(text ?? place.formatted_address);

		const placeData = Array.isArray(place) ? place[0] : place;
		if (placeData?.geometry?.location) {
			const location = placeData.geometry.location;

			if (!isNaN(Number(location.lat())) && !isNaN(Number(location.lng()))) {
				const myLatlng = new maps.LatLng(location.lat(), location.lng());

				map.panTo(myLatlng);
				marker.position = myLatlng;
				setValue({ ...getMarkerPosition(marker.position), text });
			}
		} else if (placeData?.name) {
			const [lat, lng] =
				placeData?.name.split(' ').length > 1 ? placeData.name.split(' ') : placeData.name.split(',');

			if (!lat || !lng) return;

			if (isNaN(Number(lat)) || isNaN(Number(lng))) return;

			getGeocodePosition({ lat, lng }).then(async ({ lat, lng }) => {
				const latlng = new maps.LatLng(lat, lng);
				marker.position = latlng;
				map.panTo(latlng);
				setValue({ ...getMarkerPosition(marker.position), text });
			});
		} else {
			AppLogger.error("MapField: Can't find address_components or geometry => ", inputValue);
		}
	};

	useEffect(() => {
		if (map) {
			ref && (ref.current = map);
			initMapFunctionality();
		}
	}, [map]);

	useEffect(() => {
		const setPositon = async () => {
			const latlng = new maps.LatLng(value.lat, value.lng);
			marker.position = latlng;
			map.panTo(latlng);
		};

		if (marker && value?.lat && value?.lng) {
			setPositon();
		}
	}, [value?.lat, value?.lng, marker]);

	useEffect(() => {
		if (map) {
			const topCenter = map.controls[maps.ControlPosition.TOP_CENTER];
			const topLeft = map.controls[maps.ControlPosition.TOP_LEFT];
			if (!tabletView) {
				if (topCenter.length === 0) {
					topCenter.push(inputContainer);
					topLeft.clear();
				}
			} else {
				if (topLeft.length === 0) {
					topLeft.push(inputContainer);
					topCenter.clear();
				}
			}
			map.setOptions({ mapTypeControl: !tabletView });
			return () => {
				topCenter.clear();
				topLeft.clear();
			};
		}
	}, [map]);

	const handleShowCurrentLocation = useCallback(() => {
		if (navigator.geolocation) {
			dispatch(setLoading(true));
			// eslint-disable-next-line sonarjs/no-intrusive-permissions
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const pos = {
						lat: position.coords.latitude,
						lng: position.coords.longitude
					};
					map.panTo(pos);
					map.setZoom(17);
					marker.position = pos;
					setValue(pos);
					dispatch(setLoading(false));
				},
				() => {
					// Browser can't get current location
					AppLogger.error("MapField: Browser can't get current location.");
					dispatch(setLoading(false));
				},
				{ enableHighAccuracy: true }
			);
		} else {
			// Browser doesn't support Geolocation
			AppLogger.error("MapField: Browser doesn't support Geolocation");
		}
	}, [map, marker, setValue]);

	return (
		<MapContainer>
			<Maps
				setMap={setMap}
				zoom={zoom}
				fullscreenControl={fullscreenControl}
				defaultCountry={defaultCountry}
				mapTypeControl={!tabletView}
			/>
			{showAutocomplete &&
				createPortal(
					<AddressInput
						value={inputValue}
						setInputValue={(val) => setInputValue(val)}
						placeholder="enterAlocation"
						formElement={false}
						onPlaceChange={onPlaceChange}
						restrictions={restrictions}
						disabled={disabled}
					/>,
					inputContainer
				)}
			{showLocationButton &&
				createPortal(
					<Button iconButton icon={faLocation} onClick={handleShowCurrentLocation} disabled={disabled} />,
					inputContainer
				)}
		</MapContainer>
	);
};

/**
 * @author @buunsal
 * @description This component is used to render a map for forms
 * @param {Object} props
 * @param {Object} props.value -{lat,lng}
 * @param {Function} props.setValue - {lat,lng}
 * @param {Boolean} props.showAutocomplete - If true, it will show the autocomplete input
 * @param {Boolean} props.showLocationButton - If true, it will show the location button
 * @param {Object} props.restrictions - Google maps restrictions
 * @param {Number} props.zoom - Google maps zoom
 * @param {String} props.fullscreenControl - If true, it will show the fullscreen control
 * @param {String} props.defaultCountry - Default country for the map center when initializing
 * @param {Object} ref
 *
 *
 */
export default forwardRef(MapField);
